import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {UserRolesEnum} from '../../../core/services/user/user-roles.enum';
import {CountryRestApiService} from '../../../core/services/country/http/country-rest-api.service';
import {ApiCountriesListResponse} from '../../../core/services/country/http/api-countries-list-response';
import {ApiCountryResponse} from '../../../core/services/country/http/api-country-response';
import {catchError, filter, finalize, forkJoin, map, Observable, startWith, switchMap, tap} from 'rxjs';
import {CountryService} from '../../../core/services/country/country.service';
import {HttpUtils, Utils} from '@astek/utils';
import {UserRestApiService} from '../services/http/user-rest-api.service';
import {NavigationService} from '../../../core/services/navigation/navigation.service';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {HttpErrorResponse} from '@angular/common/http';
import {TranslocoService} from '@jsverse/transloco';
import {userRoleEnumValidator} from '../../../core/services/user/validator/user-role-enum.validator';
import {Country} from '../../../core/services/country/country';
import {practitionerCountryValidator} from '../../../core/services/user/validator/practitionerCountryValidator';
import {
  practitionerHealthCentreValidator,
} from '../../../core/services/user/validator/practitionerHealthCentreValidator';
import {ApiCreateUserKnownErrorsEnum} from '../services/http/api-create-user-known-errors.enum';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HealthCenterRestApiService} from '../../health-center/services/http/health-center-rest-api.service';
import {ApiHealthCentersShortListItemResponse}
  from '../../health-center/services/http/api-health-centers-short-list-item-response';
import {HealthCenterShort} from '../../health-center/services/http/health-center-short';
import {HealthCentreService} from '../../health-center/services/health-centre.service';
import {ApiUpdateUserRequest} from '../services/http/api-update-user-request';
import {User} from '../../../core/services/user/user';
import {ActivatedRoute} from '@angular/router';
import {USER_ID_PARAMETER} from '../services/navigation/users-routes.enum';
import {UserResponse} from '../../../core/services/user/user-response';

@Component({
  selector: 'xp-update-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['../../crud.common.scss', './edit-user.component.scss'],
})
export class EditUserComponent implements OnInit {

  public readonly formGroup = this._formBuilder.group({
    email: [''],
    firstName: [''],
    lastName: [''],
    role: ['', [Validators.required.bind(Validators), userRoleEnumValidator]],
    country: new FormControl<Country | null>(null),
    healthCenter: new FormControl<HealthCenterShort | null>(null),
    profession: new FormControl<string | null>(null),
  }, {validators: [practitionerCountryValidator, practitionerHealthCentreValidator]});

  public id!: string;
  public countries: Array<Country> = [];
  public filteredCountries: Array<Country> = [];

  public healthCenters: Array<HealthCenterShort> = [];
  public filteredHealthCenters: Array<HealthCenterShort> = [];

  public isLoading: boolean = true;
  public errorKey: string = '';

  private _editedUser: User | null = null;

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _userRestApiService: UserRestApiService,
    private readonly _countryRestApiService: CountryRestApiService,
    private readonly _countryService: CountryService,
    private readonly _healthCenterRestApiService: HealthCenterRestApiService,
    private readonly _healthCenterService: HealthCentreService,
    private readonly _navigationService: NavigationService,
    private readonly _translocoService: TranslocoService,
    private readonly _snackBar: MatSnackBar,
  ) {
    this._activatedRoute.params.subscribe((params) => {
      this.id = <string>params[USER_ID_PARAMETER];
    });
  }

  public get rolesKeyValues(): Array<{ key: string, value: UserRolesEnum }> {
    return Object.entries(UserRolesEnum).map(([key, value]) => {
      return {
        key: key,
        value: value,
      };
    });
  }

  public get isNotPractitioner(): boolean {
    return this.formGroup.controls.role.value !== UserRolesEnum.PRACTITIONER;
  }

  public get userListRoute(): Array<string> {
    return ['/', RoutesEnum.USERS];
  }

  public get user(): User | null {
    return this._editedUser;
  }

  public ngOnInit(): void {
    forkJoin([
      this.initCountries(),
      this.initHealthCenters(),
    ]).pipe(
      switchMap(() => this.requestUser()), // User need to be process after init of countries/healCenters
    ).subscribe({
      error: (error: HttpErrorResponse) => this.handleError(error),
      complete: () => this.isLoading = false,
    });
  }

  public countryToString(country: Country | null): string {
    if (Utils.isNullOrUndefined(country)) {
      return '';
    }
    return country.toString();
  }

  public healthCenterToString(healthCenter: HealthCenterShort | null): string {
    if (Utils.isNullOrUndefined(healthCenter)) {
      return '';
    }
    return healthCenter.toString();
  }

  public onSubmit(): void {
    if (this.formGroup.invalid) {
      return;
    }
    this.isLoading = true;
    this.errorKey = '';
    let country: string | null = null;
    let healthCenter: string | null = null;
    if (Utils.isNotNullOrUndefined(this.formGroup.controls.country.value)) {
      country = this.formGroup.controls.country.value.alpha2Code;
    }
    if (Utils.isNotNullOrUndefined(this.formGroup.controls.healthCenter.value)) {
      healthCenter = this.formGroup.controls.healthCenter.value.id;
    }
    const data: ApiUpdateUserRequest = {
      role: this.formGroup.controls.role.value ?? '',
      firstName: this.formGroup.controls.firstName.value ?? '',
      lastName: this.formGroup.controls.lastName.value ?? '',
      country: country,
      healthCenterId: healthCenter,
      profession: this.formGroup.controls.profession.value,
    };
    this._userRestApiService.requestUpdate(data, this.id)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: async () => {
          this._snackBar.open(
            this._translocoService.translate(
              'USERS.UPDATE.SUCCESS',
              {email: this._editedUser?.email},
            ),
            '',
            {duration: 7000},
          );
          await this._navigationService.navigateTo(RoutesEnum.USERS);
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
  }

  private requestUser(): Observable<UserResponse> {
    return this._userRestApiService.requestGetUser(this.id).pipe(
      tap((userResponse: UserResponse) => {
        this._editedUser = new User().fromMessage(userResponse);

        this.formGroup.controls.firstName.setValue(this._editedUser.firstName);
        this.formGroup.controls.lastName.setValue(this._editedUser.lastName);
        this.formGroup.controls.profession.setValue(this._editedUser.profession);
        this.formGroup.controls.role.setValue(this._editedUser.roles[0]);
        const country = this.countries.find(country => country.alpha2Code === this._editedUser?.country) || null;
        this.formGroup.controls.country.setValue(country);
        const healthCenter = this.healthCenters.find(healthCenter => healthCenter.id === this._editedUser?.hospital)
          || null;
        this.formGroup.controls.healthCenter.setValue(healthCenter);

        this.formGroup.markAllAsTouched();
      }),
      catchError((error: Error) => {
        this._snackBar.open(
          this._translocoService.translate('USER.UPDATE.REQUEST_ERROR'),
          '',
          {duration: 7000},
        );
        throw error;
      }),
    );
  }

  private initCountries(): Observable<Array<Country>> {
    this.formGroup.controls.country.valueChanges.pipe(
      startWith(''),
      filter((value: any) => typeof value === 'string'),
      map((value: string) => this._countryService.filterCountryList(this.countries, value ?? '')),
    ).subscribe((countries: Array<Country>) => this.filteredCountries = countries);
    return this._countryRestApiService.requestCountryList().pipe(
      map((response: ApiCountriesListResponse) => response.countries),
      map((countries: Array<ApiCountryResponse>) => countries.map(
        (message: ApiCountryResponse) => (new Country()).fromMessage(message),
      )),
      tap((countries: Array<Country>) => this.countries = countries),
    );
  }

  private initHealthCenters(): Observable<Array<HealthCenterShort>> {
    this.formGroup.controls.healthCenter.valueChanges.pipe(
      startWith(''),
      filter((value: any) => typeof value === 'string'),
      map((value: string) => this._healthCenterService.filterHealthCentreList(this.healthCenters, value ?? '')),
    ).subscribe(
      (healthCenters: Array<HealthCenterShort>) => this.filteredHealthCenters = healthCenters,
    );

    return this._healthCenterRestApiService.requestHealthCenterShortListOfName().pipe(
      map((healthCenters: Array<ApiHealthCentersShortListItemResponse>) => healthCenters.map(
        (message: ApiHealthCentersShortListItemResponse) => (new HealthCenterShort()).fromMessage(message)),
      ),
      tap((response: Array<HealthCenterShort>) => {
        this.healthCenters = response;
      }),
    );
  }

  private handleError(error: HttpErrorResponse): void {
    if (HttpUtils.isSymfonyKnownError(error, ApiCreateUserKnownErrorsEnum)) {
      this.errorKey = 'USERS.UPDATE.' + HttpUtils.getSymfonyKnownErrorDetailMessage(error);
      return;
    }
    this.errorKey = 'SHARED.ERRORS.UNKNOWN';
  }
}
